import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Text
} from '@chakra-ui/react'
import { ReactNode, useRef, useState } from 'react'

import { ACTIONS, ActionKind } from '../ActionButton/actions'
import FormActions from '../ActionButton/FormActions'

export interface IConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  /**
   * Devolvendo promessa, o diálogo cuida do loading sozinho: gira o botão, desabilita o
   * Cancelar e barra o Esc e o clique fora até a chamada terminar. Só passe `isLoading`
   * quando o estado de carregamento for da página (porque ela também o usa em outro lugar).
   */
  onConfirm: () => void | Promise<unknown>
  /** Cor, rótulo do botão e verbo da frase vêm do registry `ACTIONS` — nunca do texto da página. */
  action?: ActionKind
  /** Substantivo do tipo de registro, minúsculo e no singular: `setor`, `obra`, `cupom`. */
  entity?: string
  /** Nome do registro. Sem ele a frase cai em "este setor" / "esta obra". */
  name?: ReactNode
  /** Concordância pt-BR: ligue para substantivo feminino (`a obra`, `esta vaga`). */
  feminine?: boolean
  /** Ação em lote. Com `count`, `entity` vai no plural: `count={3} entity="obras"`. */
  count?: number
  /** Consequência específica do registro, somada ao aviso padrão. */
  warning?: ReactNode
  /**
   * Aviso de irreversibilidade. Ligado por padrão em `delete`, que é onde ele é sempre
   * verdade — o painel tinha as duas grafias ("não pode" e "não poderá ser desfeita")
   * espalhadas, e agora existe uma só.
   */
  irreversible?: boolean
  /** Substitui a frase montada, para as confirmações que não são "verbo + registro". */
  description?: ReactNode
  /** Substitui o título montado. */
  title?: ReactNode
  /** Escape final: só onde o rótulo do registry realmente não serve. */
  confirmLabel?: string
  /** Para quando "Cancelar" ficaria ambíguo — cancelar uma inscrição, por exemplo. */
  cancelLabel?: string
  /** Redundante quando o `onConfirm` devolve promessa — some com o loading interno. */
  isLoading?: boolean
}

/** `Excluir` -> `excluir`, para a frase do corpo. O registry é a única fonte do verbo. */
function toVerb(label: string): string {
  return label.charAt(0).toLowerCase() + label.slice(1)
}

/**
 * Diálogo de confirmação do painel. Um `AlertDialog` de verdade (com `leastDestructiveRef`),
 * cópia derivada do registry `ACTIONS` e rodapé pelo `FormActions`.
 *
 * Toda confirmação passa por aqui — não monte `AlertDialog` na página, e não use
 * `window.confirm`, que bloqueia a thread e ignora o tema.
 */
export default function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  action = 'delete',
  entity,
  name,
  feminine = false,
  count,
  warning,
  irreversible,
  description,
  title,
  confirmLabel,
  cancelLabel,
  isLoading = false
}: IConfirmDialogProps) {
  // O `AlertDialog` exige um foco inicial seguro. Ele é sempre o "Cancelar", então o ref
  // vive aqui e as páginas não precisam mais carregar um `cancelRef` só para repassar.
  const cancelRef = useRef<HTMLButtonElement>(null)
  const [isConfirming, setIsConfirming] = useState(false)
  const config = ACTIONS[action]
  const showIrreversible = irreversible ?? action === 'delete'
  const isBusy = isLoading || isConfirming

  /**
   * Trinta e três das 73 chamadas do painel têm `onConfirm` assíncrono e não passam
   * `isLoading`: enquanto o DELETE estava em voo, o botão não girava nem desabilitava, e
   * dava para disparar a mesma exclusão várias vezes. Quem devolve promessa ganha o loading
   * de graça; quem não devolve segue igual.
   */
  const handleConfirm = () => {
    const result = onConfirm()

    if (!(result instanceof Promise)) return

    setIsConfirming(true)
    // `finally` e não `then(_, _)` de propósito: a rejeição continua sendo da página, e
    // engoli-la aqui esconderia o erro do console de quem não trata.
    result.finally(() => setIsConfirming(false))
  }

  const heading = title ?? [config.label, count === undefined ? entity : `${count} ${entity}`].filter(Boolean).join(' ')

  const target = (() => {
    if (!entity) {
      return null
    }

    if (count !== undefined) {
      return ` ${count} ${entity}`
    }

    if (name) {
      return (
        <>
          {feminine ? ' a ' : ' o '}
          {entity} <strong>{name}</strong>
        </>
      )
    }

    return `${feminine ? ' esta ' : ' este '}${entity}`
  })()

  return (
    <AlertDialog
      leastDestructiveRef={cancelRef}
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      /* Fechar no meio da chamada solta a tela de trás enquanto a mutação ainda está em voo —
         o recarregamento que vem depois traz o registro que já saiu da tela. `SurveyReportPage`
         precisava tratar isso à mão no próprio `onClose`. */
      closeOnEsc={!isBusy}
      closeOnOverlayClick={!isBusy}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            {heading}
          </AlertDialogHeader>

          <AlertDialogBody>
            {description ?? (
              <>
                Tem certeza que deseja {toVerb(config.label)}
                {target}?{showIrreversible && ' Esta ação não pode ser desfeita.'}
              </>
            )}
            {warning && (
              <Text mt="3" color="gray.600">
                {warning}
              </Text>
            )}
          </AlertDialogBody>

          <AlertDialogFooter>
            <FormActions
              onCancel={onClose}
              onConfirm={handleConfirm}
              confirmAction={action}
              confirmLabel={confirmLabel}
              cancelLabel={cancelLabel}
              cancelRef={cancelRef}
              isLoading={isBusy}
            />
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  )
}
